import * as React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle2,
  Clock,
  Copy,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { Button, FormRenderer } from "@eduviaos/ui-kit";
import { getCurrentTenant } from "@eduviaos/shared";
import type { FieldValidationError, PublicSubmitResponse } from "@eduviaos/shared/types";
import { fetchIntakeForm, submitEnquiry } from "@/lib/api";
import { adaptPublicFormToDefinition } from "@/lib/form-adapter";
import { parsePublicError, type PublicError } from "@/lib/error-utils";
import { PageCard } from "@/components/AppFrame";
import { ErrorState } from "@/components/ErrorState";
import { CopyableLink } from "@/components/CopyableLink";

/**
 * Intake page — the parent-facing admission enquiry form.
 *
 * Tenant resolution (D2d): the slug comes from the host name, NOT the
 * URL path. On stage that means hosts like
 * `acme-school--apply-stage.eduviaos.com`; in production it'll be
 * `acme-school--apply.eduviaos.com` (or `apply.acme-school.eduviaos.com`
 * if/when we switch to ACM nested wildcards). When no slug can be
 * resolved (bare apex, localhost) we render a calm "open your
 * institution's link" empty state so a stray Google hit doesn't 404.
 */
export function IntakePage() {
  const tenant = React.useMemo(() => getCurrentTenant(), []);
  const tenantSlug = tenant.slug ?? "";
  const [serverFieldErrors, setServerFieldErrors] = React.useState<FieldValidationError[]>([]);
  const [success, setSuccess] = React.useState<PublicSubmitResponse | null>(null);
  const honeypotRef = React.useRef<HTMLInputElement | null>(null);

  const formQuery = useQuery({
    // Including tenantSlug in the key so a tenant switcher (today
    // impossible — host-bound — but cheap insurance for tomorrow)
    // doesn't accidentally serve a cached form for the wrong tenant.
    queryKey: ["public-intake-form", tenantSlug],
    queryFn: fetchIntakeForm,
    enabled: !!tenantSlug,
  });

  const submitMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      submitEnquiry({
        values,
        honeypot: honeypotRef.current?.value ?? "",
      }),
    onSuccess: (data) => {
      setServerFieldErrors([]);
      setSuccess(data);
      // Belt-and-braces: scroll to top so the success card lands in view
      // on small screens regardless of where the submit button was.
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onError: (err) => {
      const parsed = parsePublicError(err);
      setServerFieldErrors(parsed.fieldErrors);
    },
  });

  // ── No tenant context ────────────────────────────────────────────────

  if (!tenantSlug) {
    return (
      <ErrorState
        title="Open your institution's enquiry link"
        body={
          <>
            EduViaOS Admissions runs on a per-institution surface — every school, coaching institute
            and study centre has their own apply URL (
            <code className="bg-muted/40 rounded px-1 py-0.5 font-mono text-xs">
              acme-school--apply.eduviaos.com
            </code>
            ). Open the link the institution shared with you to start an enquiry.
          </>
        }
        cta={
          <Button asChild variant="outline">
            <Link to="/welcome">About EduViaOS</Link>
          </Button>
        }
      />
    );
  }

  // ── Loading / error states ───────────────────────────────────────────

  if (formQuery.isLoading) {
    return <PendingState label="Loading enquiry form…" />;
  }

  if (formQuery.error) {
    const parsed = parsePublicError(formQuery.error);
    return <FormFetchError parsed={parsed} tenantSlug={tenantSlug} />;
  }

  const data = formQuery.data;
  if (!data) {
    return (
      <ErrorState
        title="Enquiry form unavailable"
        body="We couldn't load the form for this institution. Please refresh, or try again in a few minutes."
      />
    );
  }

  // ── Success view ─────────────────────────────────────────────────────

  if (success) {
    const trackerPath = `/track/${encodeURIComponent(success.tracking_token)}`;
    const absoluteUrl =
      typeof window !== "undefined" ? `${window.location.origin}${trackerPath}` : trackerPath;
    return (
      <SuccessView
        institutionName={data.tenant_name}
        message={success.success_message}
        trackerPath={trackerPath}
        absoluteUrl={absoluteUrl}
      />
    );
  }

  // ── Intake form ──────────────────────────────────────────────────────

  const formDefinition = adaptPublicFormToDefinition(data.form, tenantSlug);
  const submitError =
    submitMutation.isError && serverFieldErrors.length === 0
      ? parsePublicError(submitMutation.error)
      : null;

  // Phase H2 race-handling: form was open when fetched but the admin
  // (or a scheduled boundary) closed the portal between fetch and
  // submit. The BE returns 403 + code=ADMISSIONS_CLOSED with the
  // structured details; render the dedicated closed-state instead of
  // a generic banner so the parent gets the proper "next window" hint
  // and isn't tempted to retry the submit.
  if (submitError && submitError.code === "ADMISSIONS_CLOSED") {
    return <ApplyPortalClosedState parsed={submitError} />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.32, 0.72, 0.32, 1] }}
      className="grid gap-4"
    >
      <InstitutionHeader name={data.tenant_name} formName={data.form.name} />

      <PageCard
        title={data.form.name}
        subtitle="Fill in the details below. We'll send you a private link to track the enquiry."
      >
        {submitError && (
          <div
            role="alert"
            className="border-destructive/40 bg-destructive/10 text-destructive mb-5 flex items-start gap-2 rounded-md border p-3 text-sm"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{submitError.message}</span>
          </div>
        )}

        <FormRenderer
          form={formDefinition}
          serverErrors={serverFieldErrors}
          onSubmit={(payload) => submitMutation.mutate(payload)}
          submitting={submitMutation.isPending}
        />

        {/* Honeypot — invisible to humans but bots see + fill it. We
            read its value at submit time and pass it as `honeypot` in
            the BE payload so a non-empty value gets a 422. The CSS
            class is defined in index.css. */}
        <div aria-hidden className="hp-field" data-testid="honeypot-wrapper">
          <label htmlFor="hp-website">Website</label>
          <input
            ref={honeypotRef}
            id="hp-website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
          />
        </div>
      </PageCard>

      <PrivacyNote />
    </motion.div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function InstitutionHeader({ name, formName }: { name: string; formName: string }) {
  return (
    <header className="border-border/60 bg-card/60 flex items-center gap-3 rounded-xl border p-4 shadow-sm">
      <span
        aria-hidden
        className="bg-primary/10 text-primary flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
      >
        <Building2 className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <div className="text-foreground truncate text-base font-semibold leading-tight">{name}</div>
        <div className="text-muted-foreground truncate text-xs">{formName}</div>
      </div>
    </header>
  );
}

function PrivacyNote() {
  return (
    <div className="border-border/60 text-muted-foreground bg-muted/30 flex items-start gap-2 rounded-lg border p-3 text-xs">
      <ShieldCheck className="text-foreground/70 mt-0.5 h-4 w-4 flex-shrink-0" />
      <span>
        Your information is sent directly to the institution and stored on EduViaOS — we never share
        it with third parties or use it for marketing.
      </span>
    </div>
  );
}

function PendingState({ label }: { label: string }) {
  return (
    <PageCard>
      <div
        role="status"
        aria-live="polite"
        className="text-muted-foreground flex items-center gap-2 py-6 text-sm"
      >
        <Loader2 className="text-primary h-4 w-4 animate-spin" />
        <span>{label}</span>
      </div>
    </PageCard>
  );
}

function FormFetchError({ parsed, tenantSlug }: { parsed: PublicError; tenantSlug: string }) {
  if (parsed.kind === "tenant_or_form_not_found") {
    return (
      <ErrorState
        title="Enquiry form not available"
        body={
          <>
            We couldn't find an active intake form for{" "}
            <code className="font-mono">{tenantSlug}</code>. Either the institution hasn't published
            one yet, or the link you opened is from an old campaign. Please contact the institution
            to confirm the correct URL.
          </>
        }
      />
    );
  }
  // Phase H2 — apply portal lifecycle gate. The BE's
  // `_ensure_apply_portal_open` raises a 403 with code
  // `ADMISSIONS_CLOSED` and the structured details from the closed
  // window (reason, next_window, closed_message_html). Render a
  // dedicated empty-state instead of the generic error so parents
  // know exactly what happened and when the portal opens next.
  if (parsed.code === "ADMISSIONS_CLOSED") {
    return <ApplyPortalClosedState parsed={parsed} />;
  }
  if (parsed.kind === "network") {
    return (
      <ErrorState
        title="Can't reach EduViaOS"
        body={parsed.message}
        cta={<Button onClick={() => window.location.reload()}>Try again</Button>}
      />
    );
  }
  return (
    <ErrorState
      title="Couldn't load the form"
      body={parsed.message}
      cta={<Button onClick={() => window.location.reload()}>Reload</Button>}
    />
  );
}

/** Phase H2 — Apply portal closed-state empty-state.
 *
 * Renders three distinct flavours based on `details.reason`:
 *   - `portal_inactive` — admin has flipped the master switch off.
 *   - `outside_window` — schedule_kind=scheduled and no window is
 *     currently open for any grade. Shows the next window's range.
 *   - `outside_window_for_grade` — a window IS open right now for
 *     other grades, just not for the one the parent picked.
 *
 * `closed_message_html` is the IA-authored copy. We sanitise via a
 * permissive-but-safe allow-list (text + light formatting) before
 * rendering — the IA editor accepts up to 4096 chars of HTML.
 */
function ApplyPortalClosedState({ parsed }: { parsed: PublicError }) {
  const details = parsed.details ?? {};
  const reason = (details.reason as string | undefined) ?? "portal_inactive";
  const nextWindow =
    (details.next_window as
      | { start_at: string; end_at: string; label?: string | null }
      | null
      | undefined) ?? null;
  const closedHtml = (details.closed_message_html as string | null | undefined) ?? null;

  const fmtRange = (start_at: string, end_at: string) => {
    const start = new Date(start_at);
    const end = new Date(end_at);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return `${start_at} → ${end_at}`;
    }
    const fmtFull = (d: Date) =>
      d.toLocaleString(undefined, {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    return `${fmtFull(start)} → ${fmtFull(end)}`;
  };

  const title =
    reason === "outside_window_for_grade"
      ? "This grade isn't being accepted right now"
      : "Admissions are closed right now";
  const lead =
    reason === "outside_window_for_grade"
      ? "A window IS open for other grades — but not the one you selected. Pick a different grade or wait for the next window for this grade."
      : reason === "outside_window"
        ? "We're outside the current admission window. Check back during the next open window."
        : "The institution has paused new admissions for now.";

  return (
    <ErrorState
      title={title}
      body={
        <div className="space-y-4">
          <p>{lead}</p>
          {nextWindow ? (
            <div className="border-border/60 bg-muted/30 rounded-md border p-3 text-left">
              <div className="text-muted-foreground text-xs uppercase tracking-wide">
                Next open window
              </div>
              <div className="text-foreground mt-1 text-sm font-medium">
                {fmtRange(nextWindow.start_at, nextWindow.end_at)}
              </div>
              {nextWindow.label ? (
                <div className="text-muted-foreground mt-0.5 text-xs">{nextWindow.label}</div>
              ) : null}
            </div>
          ) : null}
          {closedHtml ? (
            <div
              className="border-border/60 bg-card/60 rounded-md border p-3 text-left text-sm leading-relaxed"
              data-testid="apply-portal-closed-message"
              // Iron Rule (XSS): server-side authored HTML stripped to a
              // safe allow-list. The IA settings page caps input at
              // 4096 chars and prevents non-text tags.
              dangerouslySetInnerHTML={{ __html: sanitiseClosedHtml(closedHtml) }}
            />
          ) : null}
        </div>
      }
      cta={
        <Button onClick={() => window.location.reload()} variant="outline">
          Refresh
        </Button>
      }
    />
  );
}

/** Permissive-but-safe sanitiser for the IA-authored
 * `closed_message_html`. We allow plain text formatting + links and
 * strip everything else.
 *
 * NOT a general-purpose HTML sanitiser — strictly scoped to the
 * tightly-controlled IA-author surface, where the BE already caps
 * input length and the FE renders into a sandbox-safe `<div>`.
 * If the surface ever opens up to broader input we'll swap in
 * DOMPurify; for now this avoids the dependency cost on the
 * public-admissions bundle. */
function sanitiseClosedHtml(html: string): string {
  // Strip <script>, <style>, <iframe>, <object>, <embed>, <form>,
  // and any on*="" event handler attributes. Keep <a>, <p>, <br>,
  // <strong>, <em>, <ul>/<ol>/<li>, <h1..h6>.
  const blocked =
    /<\/?(script|style|iframe|object|embed|form|meta|link|svg|video|audio|button|input|textarea|select|option)\b[^>]*>/gi;
  const onAttr = /\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi;
  const javascriptHref = /\s(href|src|action)\s*=\s*("|')\s*javascript:/gi;
  return html.replace(blocked, "").replace(onAttr, "").replace(javascriptHref, " $1=$2#");
}

function SuccessView({
  institutionName,
  message,
  trackerPath,
  absoluteUrl,
}: {
  institutionName: string;
  message: string;
  trackerPath: string;
  absoluteUrl: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.985, y: 6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.36, ease: [0.32, 0.72, 0.32, 1] }}
      className="grid gap-4"
    >
      <PageCard
        title={
          <span className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            <span>Enquiry received</span>
          </span>
        }
        subtitle={`We've passed your details to ${institutionName}. They'll be in touch soon.`}
      >
        <p className="text-foreground/90 mb-5 text-sm">{message}</p>

        <div className="border-border/60 bg-muted/40 grid gap-2 rounded-lg border p-3.5">
          <div className="text-muted-foreground flex items-center gap-1.5 text-xs uppercase tracking-wide">
            <Copy className="h-3.5 w-3.5" />
            Your private tracking link
          </div>
          <CopyableLink href={trackerPath} display={absoluteUrl} />
          <p className="text-muted-foreground mt-1 flex items-start gap-1.5 text-xs">
            <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span>
              Bookmark this — it's how you check the status of your enquiry. Anyone with the link
              can view its progress, so don't share it widely.
            </span>
          </p>
        </div>

        <div className="mt-5 flex justify-end">
          <Button asChild variant="default">
            <Link to={trackerPath}>
              View tracker
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </PageCard>
    </motion.div>
  );
}
